import React from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Image } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Called once with the captured JPEG when the parent taps "Use photo". */
  onCapture: (file: File) => void;
}

// A plain string handed to JSX renders as that DOM tag (React.createElement('video', ...)).
// Assigning it to a capitalised binding is what lets JSX accept it at all — this project has
// no "dom" lib (see the (globalThis as any) casts throughout ProfileScreen.web.tsx), so a
// literal <video>/<canvas> tag has no JSX.IntrinsicElements entry to type-check against.
const Video: any = 'video';
// Same trick, for the face-guide overlay drawn over the live preview.
const Svg: any = 'svg';
const Rect: any = 'rect';
const Defs: any = 'defs';
const Mask: any = 'mask';

// Bounding box of the guide, in the SVG's 0-100 viewBox — shared by the mask cutout
// and the outline so the two always line up.
const GUIDE_X = 22;
const GUIDE_Y = 20;
const GUIDE_SIZE = 56;
const GUIDE_RADIUS = 6;

/**
 * A dark mask over the whole frame with a square cut out over the video, plus a
 * dashed outline. Square, not the oval a passport photo would use: the photo this
 * produces is square-cropped (see handleCapture) and shown as a square everywhere
 * else in the app, so the guide should promise the shape the parent will actually get.
 */
function FaceGuideOverlay() {
  return (
    <>
      <Svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      >
        <Defs>
          <Mask id="faceGuideMask">
            <Rect width="100" height="100" fill="white" />
            <Rect x={GUIDE_X} y={GUIDE_Y} width={GUIDE_SIZE} height={GUIDE_SIZE} rx={GUIDE_RADIUS} fill="black" />
          </Mask>
        </Defs>
        <Rect width="100" height="100" fill="rgba(15, 23, 42, 0.5)" mask="url(#faceGuideMask)" />
        <Rect
          x={GUIDE_X} y={GUIDE_Y} width={GUIDE_SIZE} height={GUIDE_SIZE} rx={GUIDE_RADIUS}
          fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="0.6" strokeDasharray="2.5 2"
        />
      </Svg>
      <View style={faceGuideStyles.hintPill} pointerEvents="none">
        <Text style={faceGuideStyles.hintText}>Center your face in the frame</Text>
      </View>
    </>
  );
}

const faceGuideStyles = StyleSheet.create({
  hintPill: {
    position: 'absolute',
    bottom: 14,
    alignSelf: 'center',
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 999,
  } as any,
  hintText: { color: '#FFFFFF', fontSize: 12, fontWeight: '700' },
});

/**
 * A real in-page camera: live preview, shutter, retake/confirm — not the OS's file-picker
 * with a `capture` attribute, which does nothing on desktop browsers and behaves
 * inconsistently across mobile ones (some open the camera directly, some show a chooser).
 * getUserMedia is the one path that reliably puts an actual camera feed on screen.
 */
export function CameraCaptureModal({ visible, onClose, onCapture }: Props) {
  const { colors } = useTheme();
  const styles = React.useMemo(() => createStyles(colors), [colors]);

  const videoRef = React.useRef<any>(null);
  const streamRef = React.useRef<any>(null);
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [capturedUrl, setCapturedUrl] = React.useState<string | null>(null);
  const [capturedFile, setCapturedFile] = React.useState<File | null>(null);

  const stopStream = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((t: any) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = React.useCallback(async () => {
    const nav = (globalThis as any).navigator;
    if (!nav?.mediaDevices?.getUserMedia) {
      setError('This browser does not support camera capture. Use "Upload photo" instead.');
      return;
    }

    setError(null);
    setStarting(true);
    try {
      const stream = await nav.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err: any) {
      setError(
        err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
          ? 'Camera access was denied. Allow camera permission for this site in your browser settings and try again.'
          : err?.name === 'NotFoundError'
            ? 'No camera was found on this device.'
            : 'Could not access the camera. Please try again or use "Upload photo" instead.'
      );
    } finally {
      setStarting(false);
    }
  }, []);

  React.useEffect(() => {
    if (visible) {
      setCapturedUrl(null);
      setCapturedFile(null);
      setError(null);
      startCamera();
    } else {
      stopStream();
    }
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleCapture = () => {
    const video = videoRef.current;
    const docObj = (globalThis as any).document;
    if (!video || !docObj?.createElement) return;

    const canvas = docObj.createElement('canvas');
    // Square crop, centred — matches the round/square avatar this photo is shown in
    // everywhere else in the app, so what you frame here is what you'll actually see.
    const side = Math.min(video.videoWidth, video.videoHeight);
    if (!side) return; // stream not ready yet
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sx = (video.videoWidth - side) / 2;
    const sy = (video.videoHeight - side) / 2;
    // Mirrored to match the preview (see the video's transform below), so the
    // captured photo looks like the parent actually saw it, not flipped.
    ctx.translate(side, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, side, side, 0, 0, side, side);

    canvas.toBlob((blob: Blob | null) => {
      if (!blob) return;
      // Cast: this project has no "dom" lib, so File/Blob resolve to Node's buffer
      // typings, whose BlobOptions shape doesn't line up with the browser File
      // constructor actually running here at runtime (via react-native-web).
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' } as any);
      setCapturedFile(file);
      setCapturedUrl(URL.createObjectURL(blob));
      stopStream();
    }, 'image/jpeg', 0.92);
  };

  const handleRetake = () => {
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    setCapturedUrl(null);
    setCapturedFile(null);
    startCamera();
  };

  const handleUsePhoto = () => {
    if (capturedFile) onCapture(capturedFile);
    handleClose();
  };

  const handleClose = () => {
    stopStream();
    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    setCapturedUrl(null);
    setCapturedFile(null);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.headerIconBadge}>
                <FeatherIcon name="camera" size={16} color={colors.primary} />
              </View>
              <Text style={styles.title}>Take a photo</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={styles.closeBtn}>
              <FeatherIcon name="x" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.frame}>
            {capturedUrl ? (
              <Image source={{ uri: capturedUrl }} style={styles.frameMedia as any} />
            ) : (
              <Video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
              />
            )}

            {/* Only while the live feed is actually showing — a captured still or an
                error state has nothing to guide the parent into framing. */}
            {!capturedUrl && !starting && !error && <FaceGuideOverlay />}

            {starting && !capturedUrl && (
              <View style={styles.frameLoadingOverlay}>
                <ActivityIndicator color="#FFFFFF" />
              </View>
            )}

            {!!error && (
              <View style={styles.frameErrorOverlay}>
                <FeatherIcon name="alert-circle" size={22} color="#FCA5A5" />
                <Text style={styles.errorText}>{error}</Text>
                <TouchableOpacity style={styles.retryBtn} onPress={startCamera}>
                  <Text style={styles.retryBtnText}>Try again</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          <View style={styles.actions}>
            {capturedUrl ? (
              <>
                <TouchableOpacity style={styles.secondaryBtn} onPress={handleRetake}>
                  <FeatherIcon name="rotate-ccw" size={16} color={colors.primary} />
                  <Text style={styles.secondaryBtnText}>Retake</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.primaryBtn} onPress={handleUsePhoto}>
                  <FeatherIcon name="check" size={16} color="#FFFFFF" />
                  <Text style={styles.primaryBtnText}>Use photo</Text>
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity
                style={[styles.shutterBtn, (starting || !!error) && styles.shutterBtnDisabled]}
                onPress={handleCapture}
                disabled={starting || !!error}
              >
                <View style={styles.shutterBtnInner} />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: colors.card,
    borderRadius: 24,
    padding: 20,
    elevation: 8,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
  } as any,
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  headerIconBadge: {
    width: 30,
    height: 30,
    borderRadius: 9,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: { fontSize: 17, fontWeight: '800', color: colors.textPrimary },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.background,
    justifyContent: 'center',
    alignItems: 'center',
  },
  frame: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: '#0F172A',
    position: 'relative',
  } as any,
  frameMedia: { width: '100%', height: '100%' },
  frameLoadingOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  } as any,
  frameErrorOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    gap: 10,
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
  } as any,
  errorText: { color: '#FECACA', fontSize: 13, fontWeight: '600', textAlign: 'center', lineHeight: 18 },
  retryBtn: {
    marginTop: 4,
    backgroundColor: 'rgba(255,255,255,0.12)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 14,
  },
  retryBtnText: { color: '#FFFFFF', fontSize: 13, fontWeight: '700' },
  actions: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 14,
  },
  shutterBtn: {
    width: 64,
    height: 64,
    borderRadius: 32,
    borderWidth: 4,
    borderColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  shutterBtnDisabled: { opacity: 0.4 },
  shutterBtnInner: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.primary,
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 16,
    backgroundColor: colors.primaryLight,
  },
  secondaryBtnText: { color: colors.primary, fontSize: 14, fontWeight: '700' },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 16,
    backgroundColor: colors.primary,
  },
  primaryBtnText: { color: '#FFFFFF', fontSize: 14, fontWeight: '700' },
});
